// Screen 3: FAQ detail - category badge, bold question, rounded answer card,
// related FAQs (border cards), Learn more (accent bg cards), feedback, escalation

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@/ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getFaqById, getCategoryById, getRelatedFaqs, getLearnMoreArticles } from '../data/supportFaqData';
import MarkdownRenderer from './MarkdownRenderer';
import FeedbackWidget from './FeedbackWidget';
import EscalationSection from './EscalationSection';

interface FaqDetailProps {
  faqId: string;
  onBack: () => void;
  onSelectFaq: (faqId: string) => void;
  onSelectArticle: (articleId: string) => void;
  onContactSupport: () => void;
  isAuthenticated?: boolean;
}

const COLORS = {
  accent: '#004b49',
  accentBg: '#e7f5f4',
  accentBgHover: '#d3ece9',
  borderAccent: '#b8e3df',
  bgCard: '#ffffff',
  border: '#e5e7eb',
  borderSubtle: '#f3f4f6',
  text: '#111827',
  textSecondary: '#4b5563',
  textMuted: '#9ca3af',
  shadow: '0 1px 2px 0 rgba(0,0,0,0.04)',
};

function FaqDetail({ faqId, onBack, onSelectFaq, onSelectArticle, onContactSupport, isAuthenticated = true }: FaqDetailProps) {
  const escalationRef = useRef<HTMLDivElement>(null);
  const [showEscalation, setShowEscalation] = useState(false);

  const faq = useMemo(() => getFaqById(faqId), [faqId]);
  const category = useMemo(() => (faq ? getCategoryById(faq.categoryId) : undefined), [faq]);
  const relatedFaqs = useMemo(() => getRelatedFaqs(faqId), [faqId]);
  const learnMoreArticles = useMemo(() => getLearnMoreArticles(faqId), [faqId]);

  useEffect(() => {
    setShowEscalation(false);
  }, [faqId]);

  useEffect(() => {
    if (showEscalation) {
      escalationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showEscalation]);

  if (!faq) {
    return (
      <Box data-testid="features-support-faqs-components-faq-detail-container-action-1" sx={{ py: 6, textAlign: 'center' }}>
        <Typography data-testid="features-support-faqs-components-faq-detail-text-faq-not-found." sx={{ fontSize: '14px', color: COLORS.textSecondary, fontFamily: 'Inter, sans-serif' }}>
          FAQ not found.
        </Typography>
      </Box>
    );
  }

  const handleNegativeFeedback = () => {
    setShowEscalation(true);
  };

  const handlePositiveFeedback = () => {
    setShowEscalation(false);
  };

  return (
    <Box data-testid="features-support-faqs-components-faq-detail-container-action-2" sx={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Back button */}
      <Box data-testid="features-support-faqs-components-faq-detail-container-on-back"
        component="button"
        onClick={onBack}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 2.5,
          px: 1,
          py: 0.75,
          ml: -1,
          borderRadius: '8px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: COLORS.accent,
          fontSize: '13px',
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          '&:hover': { backgroundColor: COLORS.accentBg },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        {category?.title ?? 'Back'}
      </Box>

      {/* Category badge */}
      {category && (
        <Box data-testid="features-support-faqs-components-faq-detail-container-action-3" sx={{ mb: 2 }}>
          <Box data-testid="features-support-faqs-components-faq-detail-container-action-4"
            component="span"
            sx={{
              display: 'inline-block',
              fontSize: '11px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              borderRadius: '999px',
              backgroundColor: COLORS.accentBg,
              color: COLORS.accent,
              border: `1px solid ${COLORS.borderAccent}`,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {category.title}
          </Box>
        </Box>
      )}

      {/* Question */}
      <Typography data-testid="features-support-faqs-components-faq-detail-text-action-1"
        sx={{
          fontSize: { xs: '22px', md: '26px' },
          fontWeight: 700,
          color: COLORS.text,
          lineHeight: 1.25,
          mb: 3,
          letterSpacing: '-0.02em',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {faq.title}
      </Typography>

      {/* Answer */}
      <Box data-testid="features-support-faqs-components-faq-detail-container-action-5"
        sx={{
          mb: 5,
          p: 3,
          borderRadius: '16px',
          backgroundColor: COLORS.bgCard,
          border: `1px solid ${COLORS.border}`,
          boxShadow: COLORS.shadow,
        }}
      >
        <MarkdownRenderer content={faq.content} />
      </Box>

      {/* Related FAQ's */}
      {relatedFaqs.length > 0 && (
        <Box data-testid="features-support-faqs-components-faq-detail-container-action-6" sx={{ mb: 5 }}>
          <Typography data-testid="features-support-faqs-components-faq-detail-text-related-faqs"
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: COLORS.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Related FAQ's
          </Typography>
          <Box data-testid="features-support-faqs-components-faq-detail-container-action-7" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {relatedFaqs.map((rf) => (
              <Box data-testid="features-support-faqs-components-faq-detail-container-action-8"
                key={rf.id}
                component="button"
                onClick={() => onSelectFaq(rf.id)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2,
                  py: 1.75,
                  borderRadius: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  backgroundColor: COLORS.bgCard,
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: COLORS.shadow,
                  transition: 'all 0.15s',
                  '&:hover': {
                    backgroundColor: COLORS.accentBg,
                    borderColor: COLORS.borderAccent,
                  },
                }}
              >
                <Typography data-testid="features-support-faqs-components-faq-detail-text-action-2"
                  sx={{ flex: 1, fontSize: '14px', fontWeight: 500, color: COLORS.text, fontFamily: 'Inter, sans-serif' }}
                >
                  {rf.title}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 16, color: COLORS.textMuted, flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Learn More */}
      {learnMoreArticles.length > 0 && (
        <Box data-testid="features-support-faqs-components-faq-detail-container-action-9" sx={{ mb: 5 }}>
          <Typography data-testid="features-support-faqs-components-faq-detail-text-learn-more"
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: COLORS.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Learn more
          </Typography>
          <Box data-testid="features-support-faqs-components-faq-detail-container-action-10" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {learnMoreArticles.map((article) => (
              <Box data-testid="features-support-faqs-components-faq-detail-container-action-11"
                key={article.id}
                component="button"
                onClick={() => onSelectArticle(article.id)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2,
                  py: 1.75,
                  borderRadius: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  backgroundColor: COLORS.accentBg,
                  border: `1px solid ${COLORS.borderAccent}`,
                  transition: 'background-color 0.15s',
                  '&:hover': { backgroundColor: COLORS.accentBgHover },
                }}
              >
                <Box data-testid="features-support-faqs-components-faq-detail-container-action-12" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                  <MenuBookIcon sx={{ fontSize: 16, color: COLORS.accent, flexShrink: 0 }} />
                  <Typography data-testid="features-support-faqs-components-faq-detail-text-action-3"
                    sx={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, fontFamily: 'Inter, sans-serif' }}
                  >
                    {article.linkTitle}
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 16, color: COLORS.accent, flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Feedback */}
      <FeedbackWidget
        onNegativeFeedback={handleNegativeFeedback}
        onPositiveFeedback={handlePositiveFeedback}
      />

      {/* Escalation — shown only after negative feedback */}
      {showEscalation && (
        <Box data-testid="features-support-faqs-components-faq-detail-container-action-13" ref={escalationRef}>
          <EscalationSection onContactSupport={onContactSupport} isAuthenticated={isAuthenticated} />
        </Box>
      )}
    </Box>
  );
}

export default FaqDetail;
